import { showConfirm } from './ConfirmDialog';
import { useState, useEffect, useRef } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { useTranslation } from 'react-i18next';
import { apiGetBandFiles, apiUploadBandFile, apiUploadBandFileRaw, BAND_FILE_RAW_THRESHOLD, apiDeleteBandFile, type BandFileDTO } from '../api';
import { useWriteGate } from '../lib/writeGate';

/**
 * BandFilesSection — file store scoped to a band (or a specific show when
 * `showId` is provided). Uploads any file (read as base64 → POST /api/bands/:id/files),
 * lists them with a download link, and lets owners delete. (2026-08-11)
 */
export default function BandFilesSection({ bandId, showId, category = 'files', description, uploadHint, emptyText }: {
  bandId: string;
  showId?: number | null;
  category?: 'files' | 'promo_kit';
  /** Hint line shown next to the upload button. A component shared across
   *  CONTEXTS (a show's Files tab vs Band Settings → Files) must never hard-code
   *  one context's wording: `uploadHint` (context-specific) wins over the legacy
   *  `description` alias, and both fall back to the SHOW-scoped default key. */
  description?: string;
  uploadHint?: string;
  /** Empty-state text; falls back to the SHOW-scoped default key. Same rule:
   *  the band context must not inherit the show's "route map / venue plan" copy. */
  emptyText?: string;
}) {
  const denseRef = useRef<HTMLTableElement | null>(null);
  useDenseTableLabels(denseRef);
  const { t } = useTranslation();
  // Capability-aware write gate (lot A2). `POST|DELETE /api/bands/:id/files` are
  // mounted AFTER the server's global read-only guard (server/index.js:549) →
  // a `users.role = 'read-only'` account gets 403. Freeze the controls so the
  // impossible click (and the red « Not saved » banner) never happens.
  const writeGate = useWriteGate(bandId);
  const canWrite = writeGate.canWrite;
  const [files, setFiles] = useState<BandFileDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setFiles(await apiGetBandFiles(bandId, showId, category));
    } catch (e: any) {
      setError(e?.message || t('files.failedLoad'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, [bandId, showId, category]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPick = async (file: File) => {
    if (!canWrite) return; // frozen for a global read-only account (server 403s)
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      // >20 MB: skip the base64 round-trip — switch automatically to the RAW
      // route (server cap 200 MB; express.json would refuse the ~267 MB JSON).
      if (file.size > BAND_FILE_RAW_THRESHOLD) {
        await apiUploadBandFileRaw(bandId, file, { show_id: showId, category });
      } else {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
          reader.onerror = () => reject(new Error(t('files.couldNotRead')));
          reader.readAsDataURL(file);
        });
        await apiUploadBandFile(bandId, { filename: file.name, mime: file.type, base64, show_id: showId, category });
      }
      await load();
    } catch (e: any) {
      setError(e?.message || t('files.uploadFailed'));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (f: BandFileDTO) => {
    if (!canWrite) return; // before the confirm dialog AND the API call
    if (!(await showConfirm(t('files.deleteConfirm', { filename: f.filename })))) return;
    try {
      await apiDeleteBandFile(bandId, f.id);
      setFiles((prev) => prev.filter((x) => x.id !== f.id));
    } catch (e: any) {
      setError(e?.message || t('files.deleteFailed'));
    }
  };

  const fmtSize = (n: number) => {
    if (!n) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <input
          ref={inputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void onPick(f); }}
        />
        <button className="btn btn-primary" onClick={() => inputRef.current?.click()} disabled={uploading || !canWrite}>
          {uploading ? t('files.uploading') : t('files.uploadFile')}
        </button>
        <span className="text-dim" style={{ fontSize: 'var(--font-xs)' }}>{uploadHint ?? description ?? t('files.bandUploadHint')}{t('files.anyFile')}</span>
      </div>
      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {loading && <p className="text-dim">{t('files.loadingFiles')}</p>}
      {!loading && files.length === 0 && (
        <p className="text-dim">{emptyText ?? t('files.noBandFiles')}</p>
      )}
      {files.length > 0 && (
        <table ref={denseRef} className="table tbl-dense" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>{t('files.colFile')}</th>
              <th>{t('files.colSize')}</th>
              <th>{t('files.colUploaded')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {files.map((f) => (
              <tr key={f.id}>
                <td><a href={f.url} target="_blank" rel="noreferrer" download={f.filename}>{f.filename}</a></td>
                <td>{fmtSize(f.size)}</td>
                <td className="text-dim">{f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : ''}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn btn-danger btn-small" onClick={() => void remove(f)} disabled={!canWrite}>{t('files.delete')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}